import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { supervision, supervisionBookings } from '@cwos/api-client';
import { Pill, SkeletonSet, SuccessEmpty, TableCard, type Column } from '@cwos/ui-admin';
import { usePermissions } from '../permissions.js';
import { SettingsCard, SettingsField, SettingsHeader } from '../settings/common.js';
import {
  FORMAT_LABELS,
  emptySessionDraft,
  formatCalendarDate,
  formatWhen,
  messageOf,
  minutesLabel,
  nowLocalInput,
  sessionPayload,
  statusPill,
  toDatetimeLocalInput,
  validateSessionDraft,
  type SessionDraft,
} from './supervisionLogic.js';
import { LoadFailure } from '../LoadFailure.js';
import { SupervisionBookings } from './SupervisionBookings.js';
import { MOMENT_VARIANT, bookingGroup, whenSentence } from './supervisionBookingLogic.js';

/**
 * ONE VOCABULARY FOR A SUPERVISION ROW, SHARED WITH THE WORKER APP.
 *
 * This file used to carry its own `SESSION_STATUS_READING` map. It was written
 * to close a real defect — the Status column named THREE of the seven statuses
 * the database allows and fell through a two-branch ternary to the literal
 * `'Voided'`, so a BOOKED session, a CANCELLATION and a NO-SHOW all printed as
 * "Voided" on a compliance screen: three different facts about whether
 * supervision happened, rendered as one word, and the wrong one. It rendered
 * that way because the client's type said the status was one of three, which is
 * why the fix was a `Record` over the full union rather than another branch.
 *
 * IT IS GONE BECAUSE ITS ANSWER WAS STILL INCOMPLETE. Seven status words cannot
 * say the thing this domain turns on: a booking whose time has PASSED with
 * nobody accounting for it is the same word on the wire (`scheduled`) as a
 * booking three weeks away, and they are opposite facts. That answer depends on
 * `now`, so no map keyed on the status alone can hold it.
 *
 * `supervisionBookings.supervisionMoment` holds it, and the worker app reads
 * the same function, so a supervisor and the worker they supervise cannot be
 * shown two different accounts of one booking. Exhaustiveness is not lost: its
 * `kindOf` switches over `SupervisionSessionStatus` with no `default`, so a
 * status added upstream is still a compile error rather than a silent
 * relabelling — it just fails in one place for both apps instead of here for
 * one.
 */

/**
 * One worker's supervision: every policy that applies to them, their session
 * history, and — for whoever is allowed to act on this specific worker — the
 * record/correct form.
 *
 * PERMISSION IS NOT DECIDED HERE. The server already narrows who may reach
 * this endpoint at all (self, an assigned supervisor, or `supervision:manage`)
 * and who may record for this worker (the same assigned-supervisor edge, or
 * `supervision:manage`). Showing the form to a caller who holds
 * `supervision:record` but is not actually this worker's supervisor is safe —
 * the submit still goes through the server, and its refusal is shown verbatim.
 * The one thing worth doing client-side is not offering a supervisor picker to
 * someone who structurally cannot use it: a non-manager can only ever record as
 * themselves, which for a real supervision relationship is `worker.supervisorWorkerId`.
 */
export function SupervisionWorkerDetail() {
  const params = useParams({ strict: false }) as { workerId?: string };
  const workerId = params.workerId ?? '';
  const qc = useQueryClient();
  const { has } = usePermissions();
  const canManage = has('supervision:manage');
  const canRecord = has('supervision:record') || canManage;

  const [recording, setRecording] = useState(false);
  const [correctingId, setCorrectingId] = useState<string | null>(null);
  const [draft, setDraft] = useState<SessionDraft>(emptySessionDraft(nowLocalInput(new Date())));
  const [formError, setFormError] = useState<string | null>(null);
  const [supervisorOverride, setSupervisorOverride] = useState('');

  const detail = useQuery({
    queryKey: ['supervision', 'worker', workerId],
    queryFn: () => supervision.workerDetail(workerId),
    enabled: workerId !== '',
  });

  // Org-wide only for a manager, choosing who to attribute a session to. A
  // non-manager can only ever attribute a session to themselves, and the
  // server enforces that regardless of what this list contains.
  const supervisorOptions = useQuery({
    queryKey: ['supervision', 'workers', 'all'],
    queryFn: () => supervision.workers({ limit: 500 }),
    enabled: canManage && recording,
  });

  function resetForm() {
    setRecording(false);
    setCorrectingId(null);
    setDraft(emptySessionDraft(nowLocalInput(new Date())));
    setFormError(null);
    setSupervisorOverride('');
  }

  const record = useMutation({
    mutationFn: (supervisorWorkerId: string) =>
      supervision.recordSession(sessionPayload(workerId, supervisorWorkerId, draft)),
    onSuccess: () => {
      resetForm();
      void qc.invalidateQueries({ queryKey: ['supervision'] });
    },
    onError: (err: unknown) => setFormError(messageOf(err)),
  });

  const correct = useMutation({
    mutationFn: ({ id, supervisorWorkerId }: { id: string; supervisorWorkerId: string }) =>
      supervision.correctSession(id, sessionPayload(workerId, supervisorWorkerId, draft)),
    onSuccess: () => {
      resetForm();
      void qc.invalidateQueries({ queryKey: ['supervision'] });
    },
    onError: (err: unknown) => setFormError(messageOf(err)),
  });

  const worker = detail.data?.worker;
  const assignedSupervisorId = worker?.supervisorWorkerId ?? null;
  // A non-manager submits as the worker's ASSIGNED supervisor — if they reached
  // this screen and are not that person, the server refuses the write with a
  // specific message, which is shown as-is rather than guessed at here.
  const effectiveSupervisorId = canManage ? supervisorOverride : (assignedSupervisorId ?? '');

  const errors = validateSessionDraft(draft, new Date().toISOString());
  const canSubmit = Object.keys(errors).length === 0 && effectiveSupervisorId !== '';

  const requirementNameByPolicy = useMemo(() => {
    const m = new Map<string, string>();
    for (const p of detail.data?.policies ?? []) {
      if (p.requirementId && p.requirementName) m.set(p.requirementId, p.requirementName);
    }
    return m;
  }, [detail.data]);

  const policyColumns: Column<supervision.WorkerDetail['policies'][number]>[] = [
    { key: 'requirement', header: 'Requirement', render: (p) => p.requirementName ?? '—' },
    {
      key: 'status',
      header: 'Status',
      render: (p) => {
        const pill = statusPill(p);
        return (
          <Pill variant={pill.variant} dot>
            {pill.label}
          </Pill>
        );
      },
    },
    {
      key: 'minutes',
      header: 'This period',
      render: (p) => minutesLabel(p.minutesRequired, p.minutesRecorded),
    },
    {
      key: 'due',
      header: 'Next due / missed since',
      render: (p) =>
        p.missedPeriodStart
          ? `Missed ${formatCalendarDate(p.missedPeriodStart)}`
          : formatCalendarDate(p.nextDueOn),
    },
  ];

  /**
   * ONE `now` FOR THE WHOLE TABLE, and for the bookings card above it.
   *
   * "Has the booked time passed" is a comparison against a clock, and two rows
   * read from two clocks can land on opposite sides of it. Held in state,
   * initialised once per mount, so a re-render caused by opening a form cannot
   * silently move a booking from "still to come" to "passed" mid-interaction.
   */
  const [now] = useState(() => new Date());

  const sessionColumns: Column<supervision.SessionView>[] = [
    {
      key: 'when',
      header: 'When',
      /*
        THE VERB IS PART OF THE TIMESTAMP.

        `occurred_at` is NOT NULL, so `insertBooking` fills it with the BOOKED
        instant. This column printed it bare for all seven statuses, which said
        "supervision happened at 2pm on the 14th" for a row on which nobody has
        claimed anything happened at all — a cancelled booking included, since
        `closeBooking` leaves the timestamp alone. `supervisionMoment.sense`
        decides which sentence the instant is entitled to, from the status
        rather than from the presence of a value.
      */
      render: (s) => {
        const moment = supervisionBookings.supervisionMoment(s, now);
        return whenSentence(moment, formatWhen(moment.instant));
      },
    },
    { key: 'supervisor', header: 'Supervisor', render: (s) => s.supervisorName ?? '—' },
    {
      key: 'requirement',
      header: 'Requirement',
      render: (s) =>
        s.requirementId ? (requirementNameByPolicy.get(s.requirementId) ?? '—') : '—',
    },
    {
      key: 'duration',
      header: 'Duration',
      numeric: true,
      render: (s) => `${s.durationMinutes} min`,
    },
    { key: 'format', header: 'Format', render: (s) => FORMAT_LABELS[s.format] },
    {
      key: 'status',
      header: 'Status',
      render: (s) => {
        const moment = supervisionBookings.supervisionMoment(s, now);
        return (
          <Pill variant={MOMENT_VARIANT[moment.kind]} dot>
            {moment.label}
          </Pill>
        );
      },
    },
    {
      key: 'actions',
      header: '',
      render: (s) =>
        // `countsTowardCompliance`, NOT `status === 'recorded'`. The server
        // states this field expressly so no consumer re-derives it (see
        // `SessionView.countsTowardCompliance`), and a component inferring the
        // answer from a status word is how the map above drifted three
        // statuses behind the database in the first place. Correcting a
        // session is only offered for one that is actually standing as
        // delivered supervision.
        canRecord && s.countsTowardCompliance ? (
          <button
            type="button"
            className="rn-btn-quiet"
            onClick={() => {
              setCorrectingId(s.id);
              setRecording(false);
              setDraft({
                occurredAt: toDatetimeLocalInput(s.occurredAt),
                durationMinutes: String(s.durationMinutes),
                format: s.format,
                requirementId: s.requirementId ?? '',
              });
              setSupervisorOverride(s.supervisorWorkerId);
              setFormError(null);
            }}
          >
            Correct
          </button>
        ) : null,
    },
  ];

  if (detail.isLoading) {
    return (
      <div className="rn-col" style={{ maxWidth: 1080 }}>
        <SkeletonSet surface="table" rows={6} />
      </div>
    );
  }

  if (detail.isError || !detail.data) {
    return (
      <div className="rn-col" style={{ maxWidth: 720 }}>
        <LoadFailure
          error={detail.error}
          message="We couldn’t load this worker’s supervision record just now."
          onRetry={() => void detail.refetch()}
          mainRead
        />
      </div>
    );
  }

  const data = detail.data;
  const overall = statusPill(data.worker);

  /**
   * SETTLED ROWS ONLY.
   *
   * An OPEN booking — booked ahead, booked-and-passed, or happened-but-not-
   * confirmed — is not history and does not belong in a table captioned
   * "most recent first": `listSessions` orders by `occurred_at desc` and a
   * booking's `occurred_at` is its BOOKED instant, so a meeting three weeks in
   * the future sorted to the top of a history list. Those rows are the
   * bookings card's subject and appear there, once, with the acts that resolve
   * them. What is left here is the settled record: confirmed sessions,
   * sessions recorded without a booking, closed bookings and corrections.
   */
  const settled = data.sessions.filter(
    (s) => bookingGroup(supervisionBookings.supervisionMoment(s, now)) === null,
  );

  return (
    <div className="rn-col" style={{ maxWidth: 1080 }}>
      <SettingsHeader
        title={data.worker.displayName}
        blurb={
          data.worker.supervisorName
            ? `Supervised by ${data.worker.supervisorName}.`
            : 'No supervisor is currently assigned.'
        }
      />

      <SettingsCard>
        <div className="cmp-control-head">
          <h2 className="rn-card-title">Overall status</h2>
          <Pill variant={overall.variant} dot>
            {overall.label}
          </Pill>
        </div>
        <p className="rn-muted" style={{ marginTop: 'var(--space-6)' }}>
          {data.worker.statusMeaning}
        </p>
      </SettingsCard>

      <SettingsCard>
        <h2 className="rn-card-title">Policies that apply</h2>
        {data.policies.length === 0 ? (
          <SuccessEmpty
            heading="No requirement applies"
            message="No supervision requirement currently applies to this worker."
          />
        ) : (
          <div style={{ marginTop: 'var(--space-12)' }}>
            <TableCard
              columns={policyColumns}
              rows={data.policies}
              rowKey={(p) => p.requirementId ?? p.workerId}
            />
          </div>
        )}
      </SettingsCard>

      {/*
        BOOKED SUPERVISION — the intention, kept apart from the observed fact.

        Above the session history on purpose: a booking whose time has passed
        with nobody accounting for it is the most actionable thing on this
        screen, and it used to be invisible — sorted into a history table by a
        timestamp that was never a claim about the past.
      */}
      <SupervisionBookings
        workerId={workerId}
        worker={data.worker}
        policies={data.policies}
        sessions={data.sessions}
        canRecord={canRecord}
        managesOrgWide={canManage}
      />

      <SettingsCard>
        <div className="rn-card-head">
          <h2 className="rn-card-title">Session history</h2>
          {canRecord && !recording && !correctingId ? (
            assignedSupervisorId === null && !canManage ? null : (
              <button
                type="button"
                className="rn-btn-quiet"
                onClick={() => {
                  setRecording(true);
                  setCorrectingId(null);
                  setDraft(emptySessionDraft(nowLocalInput(new Date())));
                  setSupervisorOverride(assignedSupervisorId ?? '');
                  setFormError(null);
                }}
              >
                Record a session
              </button>
            )
          ) : null}
        </div>

        {canRecord && assignedSupervisorId === null && !canManage ? (
          <p className="rn-muted" style={{ marginTop: 'var(--space-8)' }}>
            This worker has no assigned supervisor, so supervision cannot be recorded for them yet.
            An administrator needs to assign one first.
          </p>
        ) : null}

        {recording || correctingId ? (
          <form
            className="org-create"
            onSubmit={(e) => {
              e.preventDefault();
              if (!canSubmit) return;
              if (correctingId)
                correct.mutate({ id: correctingId, supervisorWorkerId: effectiveSupervisorId });
              else record.mutate(effectiveSupervisorId);
            }}
          >
            {correctingId ? (
              <p className="rn-muted" style={{ fontSize: 13 }}>
                Correcting writes a replacement session and keeps the original as history — nothing
                is edited in place.
              </p>
            ) : null}
            <div className="org-create-grid">
              <SettingsField label="When" error={errors['occurredAt']}>
                <input
                  style={{ marginBottom: 0 }}
                  type="datetime-local"
                  value={draft.occurredAt}
                  onChange={(e) => setDraft({ ...draft, occurredAt: e.target.value })}
                />
              </SettingsField>
              <SettingsField label="Duration (minutes)" error={errors['durationMinutes']}>
                <input
                  style={{ marginBottom: 0 }}
                  type="number"
                  min={1}
                  max={1440}
                  value={draft.durationMinutes}
                  onChange={(e) => setDraft({ ...draft, durationMinutes: e.target.value })}
                />
              </SettingsField>
            </div>
            <div className="org-create-grid">
              <SettingsField label="Format">
                <select
                  style={{ marginBottom: 0 }}
                  value={draft.format}
                  onChange={(e) =>
                    setDraft({ ...draft, format: e.target.value as SessionDraft['format'] })
                  }
                >
                  <option value="in_person">In person</option>
                  <option value="video">Video</option>
                  <option value="phone">Phone</option>
                  <option value="group">Group</option>
                </select>
              </SettingsField>
              {data.policies.length > 0 ? (
                <SettingsField label="Counts toward (optional)">
                  <select
                    style={{ marginBottom: 0 }}
                    value={draft.requirementId}
                    onChange={(e) => setDraft({ ...draft, requirementId: e.target.value })}
                  >
                    <option value="">Not linked to a specific requirement</option>
                    {data.policies
                      .filter((p) => p.requirementId)
                      .map((p) => (
                        <option key={p.requirementId} value={p.requirementId!}>
                          {p.requirementName} ({p.requirementSummary})
                        </option>
                      ))}
                  </select>
                </SettingsField>
              ) : null}
            </div>

            {canManage ? (
              <SettingsField label="Delivered by">
                <select
                  style={{ marginBottom: 0 }}
                  value={supervisorOverride}
                  onChange={(e) => setSupervisorOverride(e.target.value)}
                >
                  <option value="">Choose who delivered this</option>
                  {(supervisorOptions.data?.workers ?? [])
                    .filter((w) => w.workerId !== workerId)
                    .map((w) => (
                      <option key={w.workerId} value={w.workerId}>
                        {w.displayName}
                      </option>
                    ))}
                </select>
              </SettingsField>
            ) : (
              <p className="rn-muted" style={{ fontSize: 13 }}>
                Recorded as delivered by {data.worker.supervisorName ?? 'you'} — you can only record
                supervision you delivered yourself.
              </p>
            )}

            {formError ? (
              <p style={{ color: 'var(--color-urgent-deep)', fontSize: 13 }} role="alert">
                {formError}
              </p>
            ) : null}

            <div className="set-actions">
              <button
                type="submit"
                className="signin-devbtn"
                style={{ width: 'auto', padding: 'var(--space-10) var(--space-16)' }}
                disabled={!canSubmit || record.isPending || correct.isPending}
              >
                {record.isPending || correct.isPending
                  ? 'Saving…'
                  : correctingId
                    ? 'Save correction'
                    : 'Record session'}
              </button>
              <button type="button" className="rn-btn-quiet" onClick={resetForm}>
                Cancel
              </button>
            </div>
          </form>
        ) : null}

        {settled.length === 0 ? (
          <SuccessEmpty
            heading="No sessions recorded"
            message={
              data.sessions.length === 0
                ? 'Nothing has been recorded for this worker yet.'
                : // NOT "nothing has been recorded". There ARE rows; every one
                  // of them is an open booking, which is a different fact and
                  // one the card above is showing.
                  'Nothing has been recorded yet. The bookings above have not been confirmed, so ' +
                  'none of them counts.'
            }
          />
        ) : (
          <div style={{ marginTop: 'var(--space-12)' }}>
            <TableCard
              columns={sessionColumns}
              rows={settled}
              rowKey={(s) => s.id}
              caption="Settled supervision records, most recent first. Open bookings are above."
            />
          </div>
        )}
      </SettingsCard>
    </div>
  );
}
